import { updateCollection } from '@utils/api/collections';
import { collectionConfigKey } from '@utils/collectionQueries';

/**
 * Builds and sends the `detection_objects` array for a collection PATCH.
 *
 * The endpoint replaces the whole array, so every save re-emits every object.
 * Anything not carried across is deleted server-side for every object at once,
 * which is why the rule here is: never reconstruct `style`, always spread it.
 * Callers name only the fields they are changing; colors, opacities, pointers
 * and any other key inside the free-form `style` ride along untouched. Listing
 * fields explicitly loses whatever isn't listed.
 *
 * An entry has exactly `{ node_uuid, name, filters, style, is_active }`: the API
 * returns stored entries in that shape and refuses any other field with 400, so
 * spreading a stored entry back is safe. Entries are identified by `node_uuid` and
 * carry the collection's `name` for the object. Patches are keyed by node uuid,
 * which is an enriched object's `key`.
 */

/**
 * Turns an enriched object from the detection results into a stored entry. Only
 * reached for an object the config doesn't have yet, so its `label` is the
 * detection results' name (see `useCollectionData`) — a stored entry's `name` is
 * spread forward as-is and never rewritten from the label.
 */
const entryFromObject = object => ({
	node_uuid: object.key,
	name: object.label,
	filters: object.savedFilters ?? {},
	style: { opacity: 100, ...(object.style ?? {}) },
	is_active: object.isActive ?? true,
});

/**
 * Applies one patch: shallow over the entry, one level deeper over `style`. An
 * explicit `undefined` removes that style key — absence is what means "use the
 * taxonomy default", and the optimistic cache write reads this back locally
 * where absent and undefined behave differently.
 */
const applyPatch = (entry, patch) => {
	if (!patch) return entry;

	const { style: stylePatch, ...rest } = patch;
	const merged = { ...entry, ...rest };

	if (stylePatch) {
		const style = { ...(entry.style ?? {}), ...stylePatch };
		for (const [field, value] of Object.entries(stylePatch)) {
			if (value === undefined) delete style[field];
		}
		merged.style = style;
	}

	return merged;
};

/**
 * Merges per-object patches into the collection's stored `detection_objects`.
 *
 * `configDetectionObjects` is the authoritative base — it includes objects with
 * zero detections in this run, which the results omit. `allObjects` covers
 * entries the config hasn't caught up with yet.
 *
 * Both are required rather than defaulted: with both empty this would emit a
 * payload built from patches alone, PATCHing the collection down to bare entries
 * and deleting every other object's color, filters and active flag. Callers are
 * gated on loaded data, so an empty base means something upstream is wrong and
 * failing loudly beats silently destroying the collection.
 *
 * @param {Object} patchesByKey - { [nodeUuid]: { style?, filters?, is_active? } }
 * @param {Array}  configDetectionObjects - raw `detection_objects` from the config
 * @param {Array}  allObjects - enriched objects from detection results
 * @returns {Array} complete `detection_objects` for the PATCH payload
 */
export const buildDetectionObjectsPayload = (
	patchesByKey = {},
	configDetectionObjects,
	allObjects
) => {
	const configBase = configDetectionObjects ?? [];
	const objects = allObjects ?? [];

	if (!configBase.length && !objects.length) {
		throw new Error(
			'buildDetectionObjectsPayload: refusing to build from an empty collection — ' +
				'this would delete the stored styling and filters of every object.'
		);
	}

	const seenKeys = new Set(configBase.map(entry => entry.node_uuid));
	const merged = configBase.map(entry => applyPatch(entry, patchesByKey[entry.node_uuid]));

	// Present in the results but not yet in the config — guards against a config
	// refetch that hasn't landed.
	for (const object of objects) {
		if (seenKeys.has(object.key)) continue;
		seenKeys.add(object.key);
		merged.push(applyPatch(entryFromObject(object), patchesByKey[object.key]));
	}

	return merged;
};

/**
 * Sends a set of per-object patches to the collection.
 *
 * The stored config comes from the cache, never from a prop: a prop is captured
 * in its caller's closure at the render the mutation was created in, so an
 * optimistic write that has already moved the cache on would be built over and
 * lost. Both collection-config writers go through here so that rule is stated,
 * and fixed, in one place.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} collectionUuid
 * @param {Object} patchesByKey
 * @param {Array}  allObjects - enriched objects from detection results
 */
export const saveDetectionObjectPatches = (
	queryClient,
	collectionUuid,
	patchesByKey,
	allObjects
) => {
	const cachedConfig = queryClient.getQueryData(collectionConfigKey(collectionUuid));
	const detectionObjects = buildDetectionObjectsPayload(
		patchesByKey,
		cachedConfig?.detection_objects,
		allObjects
	);
	return updateCollection(collectionUuid, { detectionObjects });
};

/**
 * Patch map that clears every object's detection pointer, returning them to their
 * taxonomy recommendation.
 *
 * @param {Array<{node_uuid: string}>} objects - stored `detection_objects` entries
 * @returns {Object} patch map for buildDetectionObjectsPayload
 */
export const buildClearPointersPatch = (objects = []) =>
	Object.fromEntries(
		(objects ?? []).map(object => [
			object.node_uuid,
			{ style: { detection_pointer: undefined } },
		])
	);
